import * as registrationApi from "app/api/registrationApi";
import { Countries, CountryID } from "app/types/registrationTypes";
import { RegistrationFormScreens } from "app/utils/const";
import { useEffect, useState } from "react";
import { injectIntl, IntlShape } from "react-intl";
import { connect } from "react-redux";
import AddressLinePostCode from "./components/AddressLinePostCode/AddressLinePostCode";
import ContactNumber from "./components/ContactNumber/ContactNumber";
import ContinueButtonContainer from "./components/ContinueButtonContainer/ContinueButtonContainer";
import CountryState from "./components/CountryState/CountryState";
import DateOfBirthGender from "./components/DateOfBirthGender/DateOfBirthGender";
import FirstNameLastName from "./components/FirstNameLastName/FirstNameLastName";
import ValidationErrorMessage from "./components/ValidationErrorMessage/ValidationErrorMessage";
import "./PersonalDetails.scss";

type PersonalDetailsProps = {
  intl: IntlShape;
  firstName: string;
  lastName: string;
  handleChange: (id: string, value: string | boolean) => void;
  dateOfBirth: string;
  gender: string;
  country: CountryID;
  state: string;
  address: string;
  building: string;
  city: string;
  countries: Countries;
  postCode: string;
  phoneNumberPrefix: string;
  phoneNumberSuffix: string;
  phoneNumber: string;
  setSelectedScreen: (selectedScreen: string) => void;
  selectedScreen: string;
  setIsCanadaSelected: (isCanadaSelected: boolean) => void;
  isCanadaSelected: boolean;
  phoneNumberPrefixId: string;
};

const PersonalDetails = (props: PersonalDetailsProps): JSX.Element => {
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [personalDetailsValidated, setPersonalDetailsValidated] =
    useState(false);
  const [
    personalDetailsValidationErrorMessage,
    setPersonalDetailsValidationErrorMessage,
  ] = useState("");
  // personal details validation errors
  const [firstNameValidationError, setFirstNameValidationError] =
    useState(false);
  const [lastNameValidationError, setLastNameValidationError] = useState(false);
  const [dateOfBirthValidationError, setDateOfBirthValidationError] =
    useState(false);
  const [genderValidationError, setGenderValidationError] = useState(false);
  const [countryValidationError, setCountryValidationError] = useState(false);
  const [stateValidationError, setStateValidationError] = useState(false);
  const [addressValidationError, setAddressValidationError] = useState(false);
  const [buildingValidationError, setBuildingValidationError] = useState(false);
  const [cityValidationError, setCityValidationError] = useState(false);
  const [postCodeValidationError, setPostCodeValidationError] = useState(false);
  const [contactNumberValidationError, setContactNumberValidationError] =
    useState(false);

  const processValidationResults = (validationResponse: any) => {
    if (validationResponse.valid) {
      setPersonalDetailsValidationErrorMessage("");
      setPersonalDetailsValidated(validationResponse.valid);
      setFirstNameValidationError(false);
      setLastNameValidationError(false);
      setDateOfBirthValidationError(false);
      setGenderValidationError(false);
      setCountryValidationError(false);
      setStateValidationError(false);
      setAddressValidationError(false);
      setBuildingValidationError(false);
      setCityValidationError(false);
      setPostCodeValidationError(false);
      setContactNumberValidationError(false);
      setErrors({});
    } else {
      // check all validation fields and set the error messages
      if (!validationResponse.firstNameValid) {
        setFirstNameValidationError(true);
      }
      if (!validationResponse.lastNameValid) {
        setLastNameValidationError(true);
      }
      if (!validationResponse.dateOfBirthValid) {
        setDateOfBirthValidationError(true);
      }
      if (!validationResponse.genderValid) {
        setGenderValidationError(true);
      }
      if (!validationResponse.countryValid) {
        setCountryValidationError(true);
      }
      if (!validationResponse.stateValid) {
        setStateValidationError(true);
      }
      if (!validationResponse.addressLine1Valid) {
        setAddressValidationError(true);
      }
      if (!validationResponse.addressLine2Valid) {
        setAddressValidationError(true);
      }

      if (!validationResponse.postCodeValid) {
        setPostCodeValidationError(true);
      }
      if (!validationResponse.contactNumberValid) {
        setContactNumberValidationError(true);
      }

      if (validationResponse.errorMessage) {
        setPersonalDetailsValidationErrorMessage(
          validationResponse.errorMessage
        );
      }
    }
  };

  const validateFormDetails = () => {
    registrationApi
      .validatePersonalDetails({
        firstName: props.firstName,
        lastName: props.lastName,
        dateOfBirth: props.dateOfBirth,
        gender: props.gender,
        country: props.country,
        state: props.state,
        addressLine1: props.isCanadaSelected ? props.building : props.address,
        addressLine2: props.isCanadaSelected ? props.address : null,
        city: props.isCanadaSelected ? props.city : "",
        postCode: props.postCode,
        contactNumber: props.phoneNumberPrefix + props.phoneNumberSuffix,
      })
      .then(response => {
        if (response.data) {
          processValidationResults(response.data);
        } else {
          setPersonalDetailsValidationErrorMessage(
            props.intl.formatMessage({
              id: "error-contact-support",
            })
          );
          setPersonalDetailsValidated(false);
        }
      })
      .catch(() => {
        setPersonalDetailsValidationErrorMessage(
          props.intl.formatMessage({
            id: "error-contact-support",
          })
        );
        setPersonalDetailsValidated(false);
      });

    return true;
  };

  useEffect(() => {
    if (personalDetailsValidated) {
      props.setSelectedScreen(RegistrationFormScreens.ACCOUNT_SETTINGS);
    }
  }, [personalDetailsValidated]);

  useEffect(() => {
    return () => {
      // Reset validation state and related error messages when leaving the page
      setPersonalDetailsValidated(false);
      setPersonalDetailsValidationErrorMessage("");
      setFirstNameValidationError(false);
      setLastNameValidationError(false);
      setDateOfBirthValidationError(false);
      setGenderValidationError(false);
      setCountryValidationError(false);
      setStateValidationError(false);
      setAddressValidationError(false);
      setPostCodeValidationError(false);
      setContactNumberValidationError(false);
      setErrors({});
    };
  }, [props.selectedScreen]);

  if (props.selectedScreen === RegistrationFormScreens.PERSONAL_DETAILS) {
    return (
      <div className="registration-form-modal__personal-details">
        <div className="form-details">
          <FirstNameLastName
            intl={props.intl}
            firstName={props.firstName}
            lastName={props.lastName}
            handleChange={props.handleChange}
            firstNameValidationError={firstNameValidationError}
            lastNameValidationError={lastNameValidationError}
          />
          <DateOfBirthGender
            intl={props.intl}
            dateOfBirth={props.dateOfBirth}
            gender={props.gender}
            handleChange={props.handleChange}
            dateOfBirthValidationError={dateOfBirthValidationError}
            genderValidationError={genderValidationError}
          />
          <CountryState
            intl={props.intl}
            country={props.country}
            state={props.state}
            handleChange={props.handleChange}
            countries={props.countries}
            countryValidationError={countryValidationError}
            stateValidationError={stateValidationError}
            setIsCanadaSelected={props.setIsCanadaSelected}
            isCanadaSelected={props.isCanadaSelected}
          />
          <AddressLinePostCode
            intl={props.intl}
            address={props.address}
            postCode={props.postCode}
            handleChange={props.handleChange}
            addressValidationError={addressValidationError}
            postCodeValidationError={postCodeValidationError}
            isCanadaSelected={props.isCanadaSelected}
            building={props.building}
            city={props.city}
            buildingValidationError={buildingValidationError}
            cityValidationError={cityValidationError}
          />
          <ContactNumber
            intl={props.intl}
            phoneNumberPrefixId={props.phoneNumberPrefixId}
            phoneNumberSuffix={props.phoneNumberSuffix}
            handleChange={props.handleChange}
            countries={props.countries}
            isCanadaSelected={props.isCanadaSelected}
            contactNumberValidationError={contactNumberValidationError}
            phoneNumber={props.phoneNumber}
          />
          {personalDetailsValidationErrorMessage && (
            <ValidationErrorMessage
              personalDetailsValidationErrorMessage={
                personalDetailsValidationErrorMessage
              }
            />
          )}
        </div>
        <ContinueButtonContainer
          setSelectedScreen={props.setSelectedScreen}
          validateFormDetails={validateFormDetails}
        />
      </div>
    );
  }
};

const mapStateToProps = state => ({
  countries: state.registrationFormModel.countries,
  isCanadaSelected: state.registrationFormModel.isCanadaSelected,
});

const mapDispatchToProps = dispatch => ({
  setIsCanadaSelected: (isSelected: boolean) =>
    dispatch.registrationFormModel.setIsCanadaSelected(isSelected),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(PersonalDetails));
